import { useCallback, useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import './Financial.css';
import { HelpPanel } from '../HelpPanel';
import { NPV_CALCULATOR, NPV_COMPARISON } from '../helpContent';
import { SelectField, PROMPT } from '@platform/ui-kit';

/**
 * Financial Modeling → NPV Calculator, and → Scenario Comparison.
 *
 * Cloned from `views/npv-value-model/calculator.ejs` and `views/npv-value-model/comparison.ejs`.
 *
 * THE DISCOUNT RATE IS ENTERED AS A PERCENTAGE AND STORED AS A FRACTION. The column is
 * `DECIMAL(5,4)`, so 10% is stored as 0.1000. The source divides by 100 on submit and this
 * does the same — sending 10 instead of 0.1 would model a 1000% discount rate and produce
 * an NPV that looks like a rounding error.
 *
 * YEAR 0 IS THE BUILD COST, UNDISCOUNTED and negative. Years 1..N each carry the same net
 * cash flow discounted by 1/(1+r)^t. There is no growth curve — the model is a flat
 * annuity, which is why `use_constant_cash_flow` defaults to true and is the only mode
 * implemented.
 *
 * The classification thresholds (100k / 25k) are HARD-CODED in the source rather than read
 * from the value-model settings, unlike the MVP ones. Shown on screen so the boundary is
 * not a mystery. Logged in docs/DEVIATIONS.md.
 */

type Dict = Record<string, unknown>;

const money = (v: unknown) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const CLASS_COLOURS: Record<string, string> = {
  High: '#166534',
  Medium: '#92400e',
  Low: '#b91c1c',
};

export function NpvCalculator() {
  const [projectId, setProjectId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [pick, setPick] = useState<Dict | null>(null);
  const [versions, setVersions] = useState<Dict[]>([]);
  const [versionId, setVersionId] = useState('');
  const [discountPct, setDiscountPct] = useState('10');
  const [horizon, setHorizon] = useState('5');
  const [basis, setBasis] = useState('ANNUAL_SAVINGS');
  const [result, setResult] = useState<Dict | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setPick(
          (await pdApi.financial.costScenarios({
            context: 'NPV',
            project: projectId || undefined,
          })) as Dict,
        );
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [projectId]);

  useEffect(() => {
    void (async () => {
      if (!opportunityId) {
        setVersions([]);
        setVersionId('');
        return;
      }
      const list = (await pdApi.financial.versions(opportunityId)).versions;
      setVersions(list);
      setVersionId(list[0] ? String(list[0].id) : '');
    })();
  }, [opportunityId]);

  const calculate = useCallback(async () => {
    if (!opportunityId || !versionId) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        (await pdApi.financial.npvCalculate({
          opportunity: opportunityId,
          version: versionId,
          // Percentage in, fraction out — see the header.
          discount_rate: String(Number(discountPct) / 100),
          horizon_years: horizon,
          cash_flow_basis: basis,
        })) as Dict,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [opportunityId, versionId, discountPct, horizon, basis]);

  const stamp = async () => {
    if (!result) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await pdApi.financial.saveNpvParameters(opportunityId, {
        npv_primary_scenario_id: Number(versionId),
        npv_discount_rate: Number(discountPct) / 100,
        npv_horizon_years: Number(horizon),
        npv_value: result.npv,
        npv_net_annual_cash_flow: result.netCashFlowPerYear,
        npv_value_classification: result.valueClassification,
      });
      setNotice('NPV written to the opportunity.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const projects = (pick?.projects as Dict[]) ?? [];
  const opportunities = (pick?.opportunities as Dict[]) ?? [];
  const table = (result?.cashFlowTable as Dict[]) ?? [];

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">NPV Calculator</h1>
          <p className="pd-page-header__subtitle">
            Discounted cash flow over a fixed horizon. Year 0 is the build cost,
            undiscounted; each later year carries the same net cash flow discounted by
            1/(1+r)ᵗ.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={NPV_CALCULATOR} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      {/* The source's selector row — `views/npv-value-model/calculator.ejs:250-266`.
          The odd one out of the three: 2px #ccc at radius 6, an explicit 40px height, and
          the groups share the row on `flex: 1` rather than carrying fixed widths. */}
      <div className="fin-selectors fin-selectors--npv">
        <div className="fin-selector fin-selector--project">
          <SelectField
            id="npv-project"
            label="Project"
            width="lg"
            placeholder={PROMPT.project}
            value={projectId}
            onChange={(v) => {
              setProjectId(v);
              setOpportunityId('');
            }}
            options={projects.map((p) => ({
              value: String(p.project_id),
              id: String(p.project_id),
              description: String(p.project_description),
            }))}
          />
        </div>

        <div className="fin-selector fin-selector--opportunity">
          <SelectField
            id="npv-opportunity"
            label="Opportunity"
            width="lg"
            value={opportunityId}
            disabled={!projectId}
            onChange={setOpportunityId}
            placeholder={
              !projectId
                ? '— Select a Project first —'
                : opportunities.length === 0
                  ? '— No Opportunities in this Project —'
                  : PROMPT.opportunity
            }
            options={opportunities.map((o) => ({
              value: String(o.opportunity_id),
              id: String(o.opportunity_id),
              description: String(o.description),
            }))}
          />
        </div>

        {versions.length ? (
          <div className="fin-selector fin-selector--version">
            <label htmlFor="npv-version">Model Version:</label>
            <select
              id="npv-version"
              value={versionId}
              onChange={(e) => setVersionId(e.target.value)}
            >
              {versions.map((v) => (
                <option key={String(v.id)} value={String(v.id)}>
                  {String(v.name)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {!opportunityId ? (
        <div className="pd-empty">Choose an opportunity to calculate its NPV.</div>
      ) : (
        <>
          <div className="fin-grid">
            <section className="pd-form-section">
              <h2 className="pd-section-title">Inputs</h2>
              <div className="pd-field">
                <label htmlFor="npv-rate">Discount rate (%)</label>
                <input
                  id="npv-rate"
                  type="number"
                  step="0.1"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(e.target.value)}
                />
                <span className="pd-hint">Stored as a fraction — 10% becomes 0.1000.</span>
              </div>
              <div className="pd-field">
                <label htmlFor="npv-horizon">Horizon (years)</label>
                <input
                  id="npv-horizon"
                  type="number"
                  value={horizon}
                  onChange={(e) => setHorizon(e.target.value)}
                />
              </div>
              <div className="pd-field">
                <label htmlFor="npv-basis">Cash flow basis</label>
                <select id="npv-basis" value={basis} onChange={(e) => setBasis(e.target.value)}>
                  <option value="ANNUAL_SAVINGS">Annual savings (run rate delta)</option>
                  <option value="ANNUAL_BUSINESS_BENEFIT">Annual business benefit</option>
                </select>
                <span className="pd-hint">
                  The source also defines a CUSTOM basis, which is a stub — it yields a zero
                  benefit, so it is not offered here.
                </span>
              </div>
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                disabled={busy || !versionId}
                onClick={calculate}
              >
                {busy ? 'Calculating…' : 'Calculate NPV'}
              </button>
            </section>

            <section className="pd-form-section">
              <h2 className="pd-section-title">Result</h2>
              {!result ? (
                <div className="pd-empty">Run the calculation to see the result.</div>
              ) : (
                <>
                  <div className="fin-npv-headline">
                    <div className="fin-kpi-label">Net present value</div>
                    <div
                      className="fin-npv-value"
                      style={{
                        color:
                          Number(result.npv) >= 0 ? '#166534' : '#b91c1c',
                      }}
                    >
                      {money(result.npv)}
                    </div>
                    <span
                      className="pd-badge"
                      style={{
                        color: CLASS_COLOURS[String(result.valueClassification)] ?? '#475569',
                      }}
                    >
                      {String(result.valueClassification)}
                    </span>
                    <div className="pd-hint">
                      High at or above $100,000; Medium at or above $25,000. These
                      thresholds are fixed in code, not in Configuration.
                    </div>
                  </div>

                  <div className="fin-kpi-grid">
                    {[
                      ['Build cost', money(result.buildCost)],
                      ['Annual operating cost', money(result.annualOperatingCost)],
                      ['Annual benefit', money(result.annualBenefit)],
                      ['Net cash flow / year', money(result.netCashFlowPerYear)],
                    ].map(([label, value]) => (
                      <div key={label} className="fin-kpi">
                        <div className="fin-kpi-label">{label}</div>
                        <div className="fin-kpi-value">{value}</div>
                      </div>
                    ))}
                  </div>
                  <p className="pd-hint">{String(result.benefitSource ?? '')}</p>

                  <button type="button" className="pd-btn" disabled={busy} onClick={stamp}>
                    Update NPV on the opportunity
                  </button>
                </>
              )}
            </section>
          </div>

          {table.length ? (
            <>
              <h2 className="pd-section-title">Cash flow</h2>
              <div className="pd-table-wrap">
                <table className="pd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Year</th>
                      <th style={{ width: 180 }}>Description</th>
                      <th style={{ width: 140 }}>Benefit</th>
                      <th style={{ width: 150 }}>Operating costs</th>
                      <th style={{ width: 150 }}>Net cash flow</th>
                      <th style={{ width: 130 }}>Discount factor</th>
                      <th style={{ width: 150 }}>Present value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.map((r) => (
                      <tr key={String(r.year)}>
                        <td className="pd-num">{String(r.year)}</td>
                        <td>{String(r.description)}</td>
                        <td className="pd-num">{money(r.benefit)}</td>
                        <td className="pd-num">{money(r.operatingCosts)}</td>
                        <td className="pd-num">{money(r.netCashFlow)}</td>
                        <td className="pd-num">{Number(r.discountFactor).toFixed(4)}</td>
                        <td className="pd-num">{money(r.presentValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

/** NPV Scenario Comparison — every NPV version of one opportunity, side by side. */
export function NpvComparison() {
  const [projectId, setProjectId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [pick, setPick] = useState<Dict | null>(null);
  const [rows, setRows] = useState<Dict[]>([]);
  const [discountPct, setDiscountPct] = useState('10');
  const [horizon, setHorizon] = useState('5');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setPick(
          (await pdApi.financial.costScenarios({
            context: 'NPV',
            project: projectId || undefined,
          })) as Dict,
        );
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [projectId]);

  const compare = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await pdApi.financial.npvComparison(
        opportunityId,
        Number(discountPct) / 100,
        Number(horizon),
      );
      setRows(r.comparisonData);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [opportunityId, discountPct, horizon]);

  useEffect(() => {
    void compare();
  }, [compare]);

  const projects = (pick?.projects as Dict[]) ?? [];
  const opportunities = (pick?.opportunities as Dict[]) ?? [];

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">NPV Scenario Comparison</h1>
          <p className="pd-page-header__subtitle">
            Every NPV scenario of one opportunity, discounted on the same terms.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={NPV_COMPARISON} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      <div className="pd-filters">
        <SelectField
          label="Project"
          width="lg"
          placeholder={PROMPT.project}
          value={projectId}
          onChange={(v) => {
            setProjectId(v);
            setOpportunityId('');
          }}
          options={projects.map((p) => ({
            value: String(p.project_id),
            id: String(p.project_id),
            description: String(p.project_description),
          }))}
        />
        <SelectField
          label="Opportunity"
          width="lg"
          placeholder={PROMPT.opportunity}
          value={opportunityId}
          disabled={!projectId}
          onChange={setOpportunityId}
          options={opportunities.map((o) => ({
            value: String(o.opportunity_id),
            id: String(o.opportunity_id),
            description: String(o.description),
          }))}
        />
        <input
          type="number"
          step="0.1"
          value={discountPct}
          onChange={(e) => setDiscountPct(e.target.value)}
          aria-label="Discount rate percent"
          style={{ minWidth: 120 }}
        />
        <input
          type="number"
          value={horizon}
          onChange={(e) => setHorizon(e.target.value)}
          aria-label="Horizon years"
          style={{ minWidth: 100 }}
        />
      </div>

      {!opportunityId ? (
        <div className="pd-empty">Choose an opportunity to compare its NPV scenarios.</div>
      ) : loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 200 }}>Scenario</th>
                <th style={{ width: 140 }}>Build cost</th>
                <th style={{ width: 160 }}>Annual operating</th>
                <th style={{ width: 140 }}>Annual benefit</th>
                <th style={{ width: 150 }}>NPV</th>
                <th style={{ width: 120 }}>Classification</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="pd-empty">
                    No NPV scenarios for this opportunity.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={String(r.versionId)}>
                    <td>{String(r.versionName)}</td>
                    <td className="pd-num">{money(r.buildCost)}</td>
                    <td className="pd-num">{money(r.annualOperatingCost)}</td>
                    <td className="pd-num">{money(r.annualBenefit)}</td>
                    <td
                      className="pd-num"
                      style={{
                        color: Number(r.npv) >= 0 ? '#166534' : '#b91c1c',
                        fontWeight: 700,
                      }}
                    >
                      {money(r.npv)}
                    </td>
                    <td>
                      <span
                        className="pd-badge"
                        style={{
                          color: CLASS_COLOURS[String(r.valueClassification)] ?? '#475569',
                        }}
                      >
                        {String(r.valueClassification)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
